import { call, delay, put, takeEvery, takeLeading } from 'redux-saga/effects';
import { OrdersApi } from 'api';
import { apiCall, prepareNotifyError } from 'store/utils';
import { getNextPageParams, sagasHandlersFactory } from 'store/entities/utils';
import { sagasHandlersFactory as featureSagasHandlersFactory } from 'store/features/utils';
import * as actions from './actions';
import * as selectors from './selectors';

const handleGetOrdersRequest = sagasHandlersFactory.createGetManyRequestHandler({
  actions: actions.getOrders,
  request: OrdersApi.getOrders,
  requestArgsBuilder: function* builder(action) {
    const { params } = action.payload;
    const { page_size, ...rest } = params;
    const { page } = yield call(getNextPageParams, page_size, selectors.ordersState, params);
    return {
      params: {
        ...rest,
        page,
        page_size,
      },
    };
  },
});

// Order-detail fetches can 404 for a few seconds right after a successful payment: `pay-order`
// responds "paid" optimistically, but the order only leaves `pending` (and becomes visible to
// GET /orders/{id}) once the payment provider's webhook + async job finalize it on the backend.
// This is a bespoke handler (not sagasHandlersFactory.createGetOneRequestHandler, used by ~30
// other entities) so the retry stays scoped to this one race condition instead of changing
// GET-one behavior app-wide. Success/failure actions are dispatched identically to the factory,
// so orderByIdState/the page component need no changes — while retries are in flight, no action
// is dispatched at all, so the UI simply keeps showing its existing loading state.
const GET_ORDER_NOT_FOUND_RETRY_DELAYS_MS = [1500, 2000, 2500, 3000, 3000];

// The backend can also respond 200 with a genuinely empty body for the same race (order exists
// but isn't finalized yet) that produces a 404 elsewhere (JPA-8146) — OrdersApi.getOrder resolves
// that case to `undefined` instead of throwing (see its definition). Never let `undefined` reach
// getOrder.success: the shared handleGetOneSuccess reducer does `byId[entity.id] = entity`
// unconditionally and would throw on a missing entity. Normalize it into a 404-shaped error
// instead, so it flows through the exact same retry/not_found path as a real 404 below.
function* fetchOrder(orderId: ReturnType<typeof actions.getOrder.request>['payload']['id']): any {
  const response = yield apiCall(OrdersApi.getOrder, orderId);
  if (response) return response;

  const emptyResponseError = new Error('Order response body was empty') as Error & {
    response?: { status: number };
  };
  emptyResponseError.response = { status: 404 };
  throw emptyResponseError;
}

export function* handleGetOrderRequest(action: ReturnType<typeof actions.getOrder.request>): any {
  const orderId = action.payload.id;

  for (let attempt = 0; ; attempt += 1) {
    try {
      const response = yield call(fetchOrder, orderId);
      yield put(actions.getOrder.success({ entity: response }));
      return;
    } catch (error) {
      if (!(error instanceof Error)) return;

      const httpStatus = (error as any).response?.status;
      const retryDelay = GET_ORDER_NOT_FOUND_RETRY_DELAYS_MS[attempt];
      if (httpStatus === 404 && retryDelay !== undefined) {
        yield delay(retryDelay);
        // eslint-disable-next-line no-continue
        continue;
      }

      const notifyError = yield prepareNotifyError(true, error, action);
      // getOrder.failure's declared payload type is narrower (`id: number`) than the request's
      // `IdType` (`string | number`) — a pre-existing mismatch in the actions factory, not
      // introduced here; createGetOneRequestHandler passes the same value through untyped.
      yield put(actions.getOrder.failure({ error, notifyError, id: orderId as unknown as number }));
      return;
    }
  }
}

const handleResendOrderEmailRequest = featureSagasHandlersFactory.createMultipleFeatureRequestHandler({
  actions: actions.resendOrderEmail,
  request: OrdersApi.resendEmail,
  requestArgsBuilder: (action) => action.payload.id,
});

export default function* ordersSagas() {
  yield takeLeading(actions.getOrders.request.type, handleGetOrdersRequest);
  yield takeEvery(actions.getOrder.request.type, handleGetOrderRequest);
  yield takeEvery(actions.resendOrderEmail.request.type, handleResendOrderEmailRequest);
}
